/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { httpClient } from './httpClient';
var detailCache = {};
var detailPromises = {};
var configPromises = {};
var entityMetaPromises = {};
var TsDataSource = /** @class */ (function () {
    function TsDataSource(_a) {
        var _b = _a === void 0 ? {} : _a, baseUrl = _b.baseUrl, client = _b.client;
        this.dataObjectHandler = {
            get: function (target, key) {
                if (key === 'objectType')
                    return target.type;
                if (key === 'id')
                    return target.id;
                return target.attributes[key];
            }
        };
        this.client = client !== null && client !== void 0 ? client : httpClient;
        this.baseUrl = baseUrl;
        this.baseUrlKey = this.baseUrl || 'default';
    }
    TsDataSource.prototype.initializeDetailCacheAndPromises = function (objectType) {
        var _a, _b, _c, _d;
        detailCache[this.baseUrlKey] = (_a = detailCache[this.baseUrlKey]) !== null && _a !== void 0 ? _a : {};
        detailCache[this.baseUrlKey][objectType] = (_b = detailCache[this.baseUrlKey][objectType]) !== null && _b !== void 0 ? _b : {};
        detailPromises[this.baseUrlKey] = (_c = detailPromises[this.baseUrlKey]) !== null && _c !== void 0 ? _c : {};
        detailPromises[this.baseUrlKey][objectType] = (_d = detailPromises[this.baseUrlKey][objectType]) !== null && _d !== void 0 ? _d : Promise.resolve();
    };
    TsDataSource.prototype.getLocalStorageKey = function (o) {
        return "".concat(o, "-").concat(this.baseUrlKey);
    };
    TsDataSource.prototype.getSavedConfig = function (key) {
        var savedConfig = JSON.parse(localStorage.getItem(key) || 'null');
        if (savedConfig === null)
            return null;
        var expiry = new Date(savedConfig['expiry']);
        return { data: savedConfig.data, expiry: expiry };
    };
    TsDataSource.prototype.isConfigExpired = function (expiry) {
        var now = new Date();
        return !(expiry && now < expiry);
    };
    TsDataSource.prototype.fetchAndSaveConfig = function (endpoint, key) {
        var anHourFromNow = new Date();
        anHourFromNow.setHours(anHourFromNow.getHours() + 1);
        if (!configPromises[key]) {
            configPromises[key] = this.client().get(endpoint, { baseURL: this.baseUrl })
                .then(function (config) {
                var savedConfig = {
                    expiry: anHourFromNow,
                    data: config.data
                };
                localStorage.setItem(key, JSON.stringify(savedConfig));
                return savedConfig.data;
            })
                .finally(function () {
                delete configPromises[key];
            });
        }
        return configPromises[key];
    };
    TsDataSource.prototype.getConfig = function (endpoint) {
        var key = this.getLocalStorageKey(endpoint);
        var savedConfig = this.getSavedConfig(key);
        if (savedConfig && !this.isConfigExpired(savedConfig.expiry)) {
            return Promise.resolve(savedConfig.data);
        }
        else {
            return this.fetchAndSaveConfig(endpoint, key);
        }
    };
    TsDataSource.prototype.attributeMetadata = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.getConfig('/_config/attribute_metadata')];
            });
        });
    };
    TsDataSource.prototype.relationshipConfig = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                return [2 /*return*/, this.getConfig('/_config/relationships')];
            });
        });
    };
    TsDataSource.prototype.addIds = function (attributes) {
        for (var _i = 0, _a = Object.values(attributes); _i < _a.length; _i++) {
            var objectType = _a[_i];
            if (!objectType.hasOwnProperty('uid')) {
                objectType['id'] = {
                    "authoritative": null,
                    "available_on_relationships": null,
                    "cardinality": 99999,
                    "description": null,
                    "display_name": null,
                    "python_type": "str"
                };
            }
        }
    };
    TsDataSource.prototype.flattenAttributes = function (attributes, relationships) {
        var _a;
        this.addIds(attributes);
        for (var entity in relationships) {
            // just deal with one-side relationships
            var oneRelationships = (_a = relationships[entity]) === null || _a === void 0 ? void 0 : _a.one;
            if (oneRelationships) {
                for (var _i = 0, _b = Object.entries(oneRelationships); _i < _b.length; _i++) {
                    var _c = _b[_i], relationship = _c[0], objType = _c[1];
                    attributes[entity]["".concat(relationship, ".id")] = {
                        available_on_relationships: true,
                        python_type: "str"
                    };
                    for (var _d = 0, _e = Object.entries(attributes[objType]); _d < _e.length; _d++) {
                        var _f = _e[_d], key = _f[0], meta = _f[1];
                        if (meta.available_on_relationships) {
                            attributes[entity]["".concat(relationship, ".").concat(key)] = meta;
                        }
                    }
                }
            }
        }
        return attributes;
    };
    TsDataSource.prototype.getEntityMeta = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                if (!entityMetaPromises[this.baseUrlKey]) {
                    entityMetaPromises[this.baseUrlKey] = (function () { return __awaiter(_this, void 0, void 0, function () {
                        var key, savedEntityMeta, expiry, now, anHourFromNow, attributes, relationships;
                        return __generator(this, function (_a) {
                            switch (_a.label) {
                                case 0:
                                    key = this.getLocalStorageKey('entityMeta');
                                    savedEntityMeta = JSON.parse(localStorage.getItem(key) || 'null');
                                    expiry = savedEntityMeta === null ? null : new Date(savedEntityMeta['expiry']);
                                    now = new Date();
                                    anHourFromNow = new Date(now);
                                    anHourFromNow.setHours(now.getHours() + 1);
                                    if (!(expiry === null || now > expiry)) return [3 /*break*/, 3];
                                    return [4 /*yield*/, this.attributeMetadata()];
                                case 1:
                                    attributes = _a.sent();
                                    return [4 /*yield*/, this.relationshipConfig()];
                                case 2:
                                    relationships = _a.sent();
                                    savedEntityMeta = {
                                        expiry: anHourFromNow,
                                        data: {
                                            flatAttributes: this.flattenAttributes(attributes, relationships),
                                            relationships: relationships
                                        }
                                    };
                                    localStorage.setItem(key, JSON.stringify(savedEntityMeta));
                                    _a.label = 3;
                                case 3: return [2 /*return*/, savedEntityMeta.data];
                            }
                        });
                    }); })().finally(function () {
                        delete entityMetaPromises[_this.baseUrlKey];
                    });
                }
                return [2 /*return*/, entityMetaPromises[this.baseUrlKey]];
            });
        });
    };
    TsDataSource.prototype.getOne = function (_a) {
        var objectType = _a.objectType, id = _a.id;
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_b) {
                this.initializeDetailCacheAndPromises(objectType);
                if (id in detailCache[this.baseUrlKey][objectType]) { // if object is in cache, return it
                    return [2 /*return*/, new Proxy(detailCache[this.baseUrlKey][objectType][id], this.dataObjectHandler)];
                }
                if (!(id in detailPromises[this.baseUrlKey][objectType])) { // if promise is not in progress, start a new one
                    detailPromises[this.baseUrlKey][objectType][id] = this.client().get("/".concat(objectType, "/").concat(id), { baseURL: this.baseUrl })
                        .then(function (response) {
                        detailCache[_this.baseUrlKey][objectType][id] = response.data.data;
                        return new Proxy(response.data.data, _this.dataObjectHandler);
                    })
                        .catch(function (error) {
                        if (error.response.status === 404)
                            return null;
                        throw error;
                    })
                        .finally(function () {
                        delete detailPromises[_this.baseUrlKey][objectType][id]; // remove promise from in-progress list when it's done
                    });
                }
                return [2 /*return*/, detailPromises[this.baseUrlKey][objectType][id]]; // return existing promise if it's in progress
            });
        });
    };
    TsDataSource.prototype.getByIds = function (_a) {
        var objectType = _a.objectType, ids = _a.ids;
        return __awaiter(this, void 0, void 0, function () {
            var promiseBulk;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.initializeDetailCacheAndPromises(objectType);
                        promiseBulk = ids.map(function (id) { return _this.getOne({ objectType: objectType, id: id }); });
                        return [4 /*yield*/, Promise.all(promiseBulk)];
                    case 1: return [2 /*return*/, _b.sent()];
                }
            });
        });
    };
    TsDataSource.prototype.getListPage = function (_a) {
        var objectType = _a.objectType, page = _a.page, pageSize = _a.pageSize, filter = _a.filter, sortBy = _a.sortBy;
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.client().get("/".concat(objectType), {
                            baseURL: this.baseUrl,
                            params: {
                                page: page,
                                page_size: pageSize,
                                filter: filter,
                                sort_by: sortBy
                            }
                        })
                            .then(function (response) {
                            detailCache[_this.baseUrlKey][objectType] = detailCache[_this.baseUrlKey][objectType] || {};
                            return response.data.data.map(function (object) {
                                detailCache[_this.baseUrlKey][objectType][object.id] = detailCache[_this.baseUrlKey][objectType][object.id] || object;
                                return new Proxy(detailCache[_this.baseUrlKey][objectType][object.id], _this.dataObjectHandler);
                            });
                        })
                            .catch(function (error) {
                            if (error.response.status === 404)
                                return null;
                            throw error;
                        })];
                    case 1: return [2 /*return*/, _b.sent()];
                }
            });
        });
    };
    return TsDataSource;
}());
export default TsDataSource;
